/**
 * Round-trippable reverse-render: a live workflow → `.canary.md` source.
 *
 * Unlike `materialize.ts` (a best-effort, read-only view for agents), this
 * emitter is the inverse of `parse` ∘ `emit` over the *authorable* slice of a
 * workflow. It stamps `workflowId:` into frontmatter and `<!-- @id <uuid> -->`
 * onto every journey node's lead-in, so the file can be edited and pushed back
 * with `canary spec compile`:
 *
 *   - node identity is keyed by `@id`, so edits update in place;
 *   - unedited nodes hash-match on recompile (see `node-hash.ts`), so the diff
 *     skips them and their recorded playbook + cache survive.
 *
 * Runtime-only data (playbookSteps, the live pageUrl an action ran against,
 * agent guidance) has no spec syntax and is intentionally dropped — it lives
 * on the server node and is preserved by the merge-on-update route, not the
 * spec.
 *
 * @module spec/pull
 */

import { topoOrderNodes } from '@chatsdet/workflow-diff';
import type { LiveWorkflow } from './diff.js';
import { renderFrontmatter } from './pull-frontmatter.js';
import { renderJourneySection } from './pull-journey.js';
import { extractPreservedFrontmatter } from './pull-preserve.js';
import {
  readInputs,
  readScenarios,
  renderDeclaredOutputs,
  renderInputsSection,
  renderScenariosSection,
} from './pull-sections.js';
import { blockquote } from './pull-values.js';

/** A live workflow plus the cosmetic extras the GET response carries. */
export type PullWorkflow = LiveWorkflow & {
  tags?: Array<{ name: string }> | null;
};

// `LiveWorkflow` already carries `teardownWorkflowName` (it's set in the GET
// workflow route) — re-exporting here would shadow it; consumers reach it via
// `PullWorkflow.teardownWorkflowName` directly.

export interface PullOptions {
  /**
   * Force a `requirementRef` value (e.g. a ticket URL). When omitted, the CLI
   * preserves the prior file's value via `preserveFrontmatter`.
   */
  requirementRef?: string;
  /**
   * Per-scenario `skipReason` overrides keyed by scenario name. Spec-source-only;
   * the CLI seeds this from the prior file so re-pull doesn't drop the notes.
   */
  preserveScenarioSkipReasons?: Record<string, string>;
}

export { extractPreservedFrontmatter };

export function workflowToSpec(workflow: PullWorkflow, opts: PullOptions = {}): string {
  const lines: string[] = [];
  const sorted = topoOrderNodes(workflow.nodes, workflow.edges ?? []);
  const inputs = readInputs(workflow.inputsJson);
  const inputNames = new Set(inputs.map((i) => i.name));
  // The auth login is the first login node, skipping any leading seed/setup
  // nodes that provision data before authentication. It's represented by the
  // `actor`/`authenticated` frontmatter (emit re-injects it at this position),
  // not as a journey node.
  let firstNonSeed = 0;
  while (
    firstNonSeed < sorted.length &&
    (sorted[firstNonSeed].nodeType === 'seed' || sorted[firstNonSeed].nodeType === 'setup')
  ) {
    firstNonSeed++;
  }
  const authLogin = sorted[firstNonSeed]?.nodeType === 'login' ? sorted[firstNonSeed] : undefined;

  lines.push(...renderFrontmatter(workflow, authLogin, opts));
  lines.push(`# ${workflow.name}`);
  lines.push('');

  // ── Spirit / Preflight / Cleanup ─────────────────────────────────────────
  if (workflow.description) {
    lines.push('## Spirit');
    lines.push(blockquote(workflow.description));
    lines.push('');
  }
  if (workflow.preflight) {
    lines.push('## Preflight');
    lines.push(workflow.preflight.trim());
    lines.push('');
  }
  if (workflow.cleanup) {
    lines.push('## Cleanup');
    lines.push(workflow.cleanup.trim());
    lines.push('');
  }

  const inputLines = renderInputsSection(inputs);
  if (inputLines) lines.push(...inputLines);

  const scenarios = readScenarios(workflow.scenariosJson);
  const scenarioLines = renderScenariosSection(
    scenarios,
    opts.preserveScenarioSkipReasons ?? {}
  );
  if (scenarioLines) lines.push(...scenarioLines);

  const declaredOutputLines = renderDeclaredOutputs(workflow.seedOutputs);
  if (declaredOutputLines) lines.push(...declaredOutputLines);

  lines.push(...renderJourneySection(sorted, authLogin, inputNames));

  while (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
  lines.push('');
  return lines.join('\n');
}
